export const capitalizeWords = (text: string): string => {
  return text.replace(/\b\w+\b/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};
export const isNil = (value: any): boolean => value === null || value === undefined;
export const toNumberOrMinusOne = (value: string | number): number => {
  return value === '' ? -1 : Number(value);
};
export const emailRegexp = new RegExp(/^[\w.+-]+@[a-zA-Z_-]+?\.[a-zA-Z]{2,}$/);
export const swiftRegexp = new RegExp(/^[A-Za-z]{4}[A-Za-z]{2}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/);
export const ibanRegexp = new RegExp(/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/);
// eslint-disable-next-line

export const webSiteUrlRegex = new RegExp(
  /^(?:(?:https?|ftp):\/\/)?(?:\S+?@)?(?:(?!10(?:\.\d{1,3}){3})(?!127(?:\.\d{1,3}){3})(?!169\.254(?:\.\d{1,3}){2})(?!192\.168(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:\d{1,3}\.){3}\d{1,3}|(?:(?:[\p{L}\d](?:[\p{L}\d-]{0,61}[\p{L}\d])?\.)+[\p{L}]{2,}))(?::\d{2,5})?(?:\/\S*)?$/u
);
export const zipCodeRegex = /^[A-Za-z0-9\- ]{3,10}$/;

export const makeNiceName = (string: string) =>
  string && (string?.[0].toUpperCase() + string.slice(1)).replace(/_/g, ' ');

export function screamingSnakeToTitleCase(input?: string | null | undefined): string {
  if (!input) return '';

  return input
    .toLowerCase()
    .split('_')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const convertToTitleCase = (string: string) =>
  string && string.replace(/([A-Z])/g, ' $1').replace(/^./, (match) => match.toUpperCase());
export const blockInvalidChar = (e: React.KeyboardEvent<HTMLInputElement>, type: string | undefined) => {
  if (type === 'number') {
    ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault();
  }
};
export const numberWithCommas = (value: number | string) => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
export const isObjectEmpty = (object: any) => !Object.values(object || {}).length;

export const cleanObject = (obj: Record<string, any> | null | undefined) => {
  if (!obj) return undefined;

  const filtered = Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== null && value !== undefined && value !== '')
  );

  return Object.keys(filtered).length ? filtered : undefined;
};

export const isNumeric = (value: any) => {
  return !isNaN(value) && !isNaN(parseFloat(value));
};

export const generateUniqueId = (): string => {
  const timestamp = Date.now();
  const randomNum = Math.floor(Math.random() * 1000000);
  return `${timestamp}-${randomNum}`;
};

export const getUpdatedPayload = <T extends Record<string, any>>(
  values: T,
  dataToEdit?: Partial<T>,
  keysToSkip?: string[]
): Partial<{ [K in keyof T]: T[K] | undefined }> => {
  const skipKeys = keysToSkip || [];

  const isDeepEqual = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) return false;

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    return keysA.every((key) => isDeepEqual(a[key], b[key]));
  };

  return Object.keys(values).reduce(
    (acc, key) => {
      const typedKey = key as keyof T;
      if (skipKeys.includes(typedKey as string)) return acc;

      let newValue = values[typedKey];
      const editValue = dataToEdit?.[typedKey];

      // Handle empty values based on type
      if ((newValue === null || newValue === undefined) && editValue !== null && editValue !== undefined) {
        if (typeof dataToEdit?.[typedKey] === 'number' || typeof values[typedKey] === 'number') {
          newValue = -1 as T[keyof T];
        } else if (typeof dataToEdit?.[typedKey] === 'string' || typeof values[typedKey] === 'string') {
          newValue = '' as T[keyof T];
        } else if (Array.isArray(dataToEdit?.[typedKey]) || Array.isArray(values[typedKey])) {
          newValue = [] as T[keyof T];
        } else if (typeof dataToEdit?.[typedKey] === 'object' || typeof values[typedKey] === 'object') {
          newValue = {} as T[keyof T];
        }
      }

      // Deep compare arrays & objects
      const isChanged =
        Array.isArray(newValue) || typeof newValue === 'object'
          ? !isDeepEqual(newValue, dataToEdit?.[typedKey])
          : newValue !== dataToEdit?.[typedKey];

      acc[typedKey] = isChanged ? newValue : undefined;
      return acc;
    },
    {} as Partial<{ [K in keyof T]: T[K] | undefined }>
  );
};
export const preventNonNumericValue = (event: any) => {
  const inputValue = event.data;
  const numericRegex = new RegExp(/^-?[0-9.,]*$/);
  if (!numericRegex.test(inputValue)) {
    event.preventDefault(); // Prevent invalid input
  }
};

export const listShouldShowSeeMore = <T>(value: T[] = [], maxVisibleItems: number = 6): boolean => {
  return value.length > maxVisibleItems;
};

export const getLatestSource = (listTimeStamp: number, filterTimeStamp: number, searchTimeStamp: number) => {
  const latestTimeStamp = Math.max(listTimeStamp || 0, filterTimeStamp || 0, searchTimeStamp || 0);
  let source: string | null = '';

  switch (latestTimeStamp) {
    case listTimeStamp:
      source = 'list';
      break;
    case filterTimeStamp:
      source = 'filter';
      break;
    case searchTimeStamp:
      source = 'search';
      break;
    default:
      source = null;
  }
  return source;
};

export const convertEnumsToReadableStrings = (string: string) => {
  if (!string) return '';
  return string
    .replace(/([0-9]+)/g, ' $1') // Add space before numbers
    .toLowerCase()
    .split(/[_\s]+/) // Split on underscores or existing spaces
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const convertStringsToOptionsObject = (input: string[]): { value: string; label: string }[] => {
  return Array.from(new Set(input)) // remove duplicates
    .map((item) => ({
      value: item,
      label: convertEnumsToReadableStrings(item),
    }));
};

export const formatFileSize = (bytes: number): string => {
  const sizeInKB = bytes / 1024;

  if (sizeInKB > 1000) {
    const sizeInMB = sizeInKB / 1024;
    return `${sizeInMB.toFixed(2)} MB`;
  } else {
    return `${sizeInKB.toFixed(2)} KB`;
  }
};

// Helper function to convert base64 to Blob
export function base64ToBlob(base64: string): Blob {
  const parts = base64.split(';base64,');
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: contentType });
}

// Helper function to extract MIME type from base64 string
export function getMimeTypeFromBase64(base64: string): string {
  const match = base64.match(/^data:([^;]+);base64,/);
  return match ? match[1] : 'image/jpeg'; // Default to jpeg if unable to determine
}

// Helper function to get file extension from MIME type
export function getExtensionFromMimeType(mimeType: string): string {
  const mimeToExtension: { [key: string]: string } = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/bmp': 'bmp',
    'image/svg+xml': 'svg',
    'image/tiff': 'tiff',
    'image/heic': 'heic',
    'image/heif': 'heif',
  };
  return mimeToExtension[mimeType] || 'jpg'; // Default to jpg if unknown
}
